import { AuthProvider, UserRole } from '@fsp/types';
import { Alert, Typography, IconButton } from '@mui/material';
import { ArrowUpward } from '@mui/icons-material';
import React, { useState } from 'react';
import PaperSection from '../../components/PaperSection';
import { useAuth } from '../../contexts/AuthContext';
import ContentWrapper from '../../layout/ContentWrapper';
import GoogleAccountLink from './providers/GoogleAccountLink';
import ResetPassword from './providers/ResetPassword';
import SetPassword from './providers/SetPassword';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

export const SecurityRoles = [
  UserRole.fsp,
  UserRole.OWNER,
  UserRole.ADMIN,
  UserRole.MEMBER,
  UserRole.VIEWER,
  UserRole.AUDITOR,
  UserRole.APPROVER,
];

interface OAuthError {
  error: string;
  provider: AuthProvider;
}

const Security: React.FC = () => {
  const [oauthError, setOauthError] = useState<OAuthError | null>(null);
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const clearError = () => {
    setOauthError(null);
  };

  const handleClose = () => {
    navigate('/settings/');
  };

  const headerActions = (
    <IconButton onClick={handleClose}>
      <ArrowUpward />
    </IconButton>
  );

  if (!user) {
    return (
      <ContentWrapper breadcrumb>
        <PaperSection title="Connected Accounts">Loading...</PaperSection>
      </ContentWrapper>
    );
  }
  const handleOAuthError = (error: string, provider: AuthProvider) => {
    setOauthError({ error, provider });
  };

  const handleOAuthSuccess = (message: string, provider: AuthProvider) => {
    enqueueSnackbar(message, { variant: 'success' });
  };

  return (
    <ContentWrapper breadcrumb>
      <PaperSection title="Connected Accounts" action={headerActions}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Link your accounts to enable single sign-on with the following trusted providers.
        </Typography>

        {oauthError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setOauthError(null)}>
            {oauthError.error}
          </Alert>
        )}

        <GoogleAccountLink
          onError={handleOAuthError}
          onClearError={clearError}
          onSuccess={handleOAuthSuccess}
        />
      </PaperSection>
      {user.providers && user.providers.includes(AuthProvider.PASSWORD) && <ResetPassword />}
      {user.providers && !user.providers.includes(AuthProvider.PASSWORD) && <SetPassword />}
    </ContentWrapper>
  );
};

export default Security;
